import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { Baby, User as UserIcon } from 'lucide-preact';
import { api, type Child, type EventDetail, type Me, type UserBasic } from '../lib/api.js';

type Mode = 'child' | 'self';
type Preset = 'bring_heim' | 'bring_bleibt' | 'abholen_heim' | 'komplett' | 'custom';

function presetSlot(preset: Preset): {
  role: string;
  startsAtAnchor: 'event_start' | 'event_end';
  startsAtOffsetMin: number;
  endsAtAnchor: 'event_start' | 'event_end';
  endsAtOffsetMin: number;
} {
  switch (preset) {
    case 'bring_heim': return { role: 'bring', startsAtAnchor: 'event_start', startsAtOffsetMin: -20, endsAtAnchor: 'event_start', endsAtOffsetMin: 20 };
    case 'bring_bleibt': return { role: 'bleibt_dabei', startsAtAnchor: 'event_start', startsAtOffsetMin: -20, endsAtAnchor: 'event_end', endsAtOffsetMin: 0 };
    case 'abholen_heim': return { role: 'holt_ab', startsAtAnchor: 'event_end', startsAtOffsetMin: 0, endsAtAnchor: 'event_end', endsAtOffsetMin: 20 };
    case 'komplett': return { role: 'kümmert_sich_komplett', startsAtAnchor: 'event_start', startsAtOffsetMin: -20, endsAtAnchor: 'event_end', endsAtOffsetMin: 20 };
    case 'custom': return { role: 'bleibt_dabei', startsAtAnchor: 'event_start', startsAtOffsetMin: 0, endsAtAnchor: 'event_end', endsAtOffsetMin: 0 };
  }
}

function roundTo(d: Date, toMin: number): Date {
  const ms = toMin * 60_000;
  return new Date(Math.round(d.getTime() / ms) * ms);
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

type Props = { me: Me };

export function EventCreate({ me }: Props) {
  const loc = useLocation();
  const [mode, setMode] = useState<Mode>('child');
  const [children, setChildren] = useState<Child[]>([]);
  const [selectedChild, setSelectedChild] = useState<string>('');
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const startDefault = roundTo(new Date(Date.now() + 3600_000), 15);
  const endDefault = roundTo(new Date(Date.now() + 7200_000), 15);
  const [startsAt, setStartsAt] = useState(toLocalInput(startDefault));
  const [endsAt, setEndsAt] = useState(toLocalInput(endDefault));

  function onStartsChange(v: string) {
    setStartsAt(v);
    const ns = new Date(v);
    const ne = new Date(endsAt);
    if (isNaN(ns.getTime())) return;
    if (isNaN(ne.getTime()) || ne.getTime() <= ns.getTime() || ne.getTime() - ns.getTime() < 15 * 60_000) {
      setEndsAt(toLocalInput(new Date(ns.getTime() + 60 * 60_000)));
    }
  }

  function onEndsChange(v: string) {
    const ne = new Date(v);
    const ns = new Date(startsAt);
    if (!isNaN(ne.getTime()) && !isNaN(ns.getTime()) && ne.getTime() <= ns.getTime()) {
      setEndsAt(toLocalInput(new Date(ns.getTime() + 60 * 60_000)));
      return;
    }
    setEndsAt(v);
  }
  const [recurs, setRecurs] = useState(false);
  const [recRule, setRecRule] = useState('FREQ=WEEKLY;BYDAY=SA');
  const [bringerPreset, setBringerPreset] = useState<Preset>('bring_heim');
  const [bringerUserId, setBringerUserId] = useState<string>(me.id);
  const [abholerPreset, setAbholerPreset] = useState<Preset>('abholen_heim');
  const [abholerUserId, setAbholerUserId] = useState<string>(me.id);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<UserBasic[]>([]);

  useEffect(() => {
    api<Child[]>('/api/v1/children').then(setChildren).catch(() => setChildren([]));
    api<UserBasic[]>('/api/v1/users')
      .then((list) => {
        // "ich" nach vorne + Suffix
        const sorted = [...list].sort((a, b) => (a.id === me.id ? -1 : b.id === me.id ? 1 : a.displayName.localeCompare(b.displayName)));
        setUsers(sorted.map((u) => (u.id === me.id ? { ...u, displayName: u.displayName + ' (ich)' } : u)));
      })
      .catch(() => setUsers([{ ...me, displayName: me.displayName + ' (ich)' }]));
  }, []);

  useEffect(() => {
    if (!selectedChild && children.length > 0) setSelectedChild(children[0]!.id);
  }, [children]);

  async function submit(e: Event) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const startsIso = new Date(startsAt).toISOString();
      const endsIso = new Date(endsAt).toISOString();
      const participants: Array<Record<string, unknown>> = [];
      if (mode === 'child') {
        // Kind-Event
        if (selectedChild) {
          participants.push({
            actorType: 'child',
            actorChildId: selectedChild,
            role: 'child_owner',
            acceptStatus: 'auto',
            startsAtAnchor: 'event_start',
            startsAtOffsetMin: 0,
            endsAtAnchor: 'event_end',
            endsAtOffsetMin: 0,
          });
        }
        // Bringer
        if (bringerUserId) {
          const s = presetSlot(bringerPreset);
          participants.push({
            actorType: 'user',
            actorUserId: bringerUserId,
            role: s.role,
            acceptStatus: bringerUserId === me.id ? 'auto' : 'pending',
            startsAtAnchor: s.startsAtAnchor,
            startsAtOffsetMin: s.startsAtOffsetMin,
            endsAtAnchor: s.endsAtAnchor,
            endsAtOffsetMin: s.endsAtOffsetMin,
          });
        }
        // Abholer (nur wenn nicht komplett)
        if (abholerUserId && bringerPreset !== 'komplett' && bringerPreset !== 'bring_bleibt') {
          const s = presetSlot(abholerPreset);
          participants.push({
            actorType: 'user',
            actorUserId: abholerUserId,
            role: s.role,
            acceptStatus: abholerUserId === me.id ? 'auto' : 'pending',
            startsAtAnchor: s.startsAtAnchor,
            startsAtOffsetMin: s.startsAtOffsetMin,
            endsAtAnchor: s.endsAtAnchor,
            endsAtOffsetMin: s.endsAtOffsetMin,
          });
        }
      } else {
        // Self-Event: minimal
        participants.push({
          actorType: 'user',
          actorUserId: me.id,
          role: 'bleibt_dabei',
          acceptStatus: 'auto',
          startsAtAnchor: 'event_start',
          startsAtOffsetMin: 0,
          endsAtAnchor: 'event_end',
          endsAtOffsetMin: 0,
        });
      }
      const payload: Record<string, unknown> = {
        title,
        startsAt: startsIso,
        endsAt: endsIso,
        location: location || null,
        childIds: mode === 'child' && selectedChild ? [selectedChild] : [],
        recurrenceRule: recurs ? recRule : null,
        participants,
      };
      const created = await api<EventDetail>('/api/v1/events', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      loc.route(`/event/${created.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="screen">
      <h1>Neuer Termin</h1>
      <div class="row">
        <button class={mode === 'child' ? '' : 'ghost'} onClick={() => setMode('child')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <Baby size={18} /> Kinder
        </button>
        <button class={mode === 'self' ? '' : 'ghost'} onClick={() => setMode('self')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <UserIcon size={18} /> Eigen
        </button>
      </div>

      <form onSubmit={submit} style={{ marginTop: 16 }}>
        {mode === 'child' ? (
          <div class="field">
            <label>Kind</label>
            {children.length === 0 ? (
              <p class="muted">Keine Kinder angelegt. Erst in Einstellungen.</p>
            ) : (
              <select value={selectedChild} onInput={(e) => setSelectedChild((e.target as HTMLSelectElement).value)}>
                {children.map((c) => <option value={c.id}>{c.name}</option>)}
              </select>
            )}
          </div>
        ) : null}

        <div class="field">
          <label>Titel</label>
          <input required value={title} onInput={(e) => setTitle((e.target as HTMLInputElement).value)} placeholder="z.B. Fußballtraining" />
        </div>

        <div class="field">
          <label>Ort</label>
          <input value={location} onInput={(e) => setLocation((e.target as HTMLInputElement).value)} placeholder="optional" />
        </div>

        <div class="row">
          <div class="field">
            <label>Von</label>
            <input type="datetime-local" required value={startsAt} onInput={(e) => onStartsChange((e.target as HTMLInputElement).value)} />
          </div>
          <div class="field">
            <label>Bis</label>
            <input type="datetime-local" required value={endsAt} min={startsAt} onInput={(e) => onEndsChange((e.target as HTMLInputElement).value)} />
          </div>
        </div>

        <div class="field">
          <label><input type="checkbox" checked={recurs} onChange={(e) => setRecurs((e.target as HTMLInputElement).checked)} /> wiederkehrend</label>
          {recurs ? (
            <input value={recRule} onInput={(e) => setRecRule((e.target as HTMLInputElement).value)} placeholder="FREQ=WEEKLY;BYDAY=SA" />
          ) : null}
        </div>

        {mode === 'child' ? (
          <>
            <div class="field">
              <label>Bringer</label>
              <select value={bringerUserId} onInput={(e) => setBringerUserId((e.target as HTMLSelectElement).value)}>
                {users.map((u) => <option value={u.id}>{u.displayName}</option>)}
              </select>
              <select value={bringerPreset} onInput={(e) => setBringerPreset((e.target as HTMLSelectElement).value as Preset)} style={{ marginTop: 6 }}>
                <option value="bring_heim">Bringt und fährt heim (-20/+20)</option>
                <option value="bring_bleibt">Bringt und bleibt dabei</option>
                <option value="komplett">Kümmert sich komplett</option>
                <option value="custom">Benutzerdefiniert (v1: Defaults 0/0)</option>
              </select>
            </div>
            {bringerPreset !== 'komplett' && bringerPreset !== 'bring_bleibt' ? (
              <div class="field">
                <label>Abholer</label>
                <select value={abholerUserId} onInput={(e) => setAbholerUserId((e.target as HTMLSelectElement).value)}>
                  {users.map((u) => <option value={u.id}>{u.displayName}</option>)}
                </select>
                <select value={abholerPreset} onInput={(e) => setAbholerPreset((e.target as HTMLSelectElement).value as Preset)} style={{ marginTop: 6 }}>
                  <option value="abholen_heim">Holt ab und fährt heim (0/+20)</option>
                </select>
              </div>
            ) : null}
          </>
        ) : null}

        {error ? <div class="error">{error}</div> : null}
        <button disabled={saving} type="submit" style={{ width: '100%', marginTop: 10 }}>
          {saving ? <span class="spinner" /> : 'Anlegen'}
        </button>
      </form>
    </div>
  );
}
